/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { IAssessmentsConfig, ITasksConfig } from 'app/core/models/essette-interfaces';
import { bindable } from 'aurelia-typed-observable-plugin';
import { AssessmentService } from 'app/core/services/assessment-service';
import { CaseService } from 'app/cm/services/case-service';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { ICollapse } from 'app/shared/attributes/ess-collapse-attributes';
import { apiCall } from 'app/shared/lib/essette-api';
import * as CM from 'app/cm/models/essette-api';
import * as Core from 'app/core/models/essette-api';
import { ExtendLocalState } from 'app/shared/models/local-state-extensions';
import { guid } from 'app/shared/lib/guid';

type AttachmentWithLocalState = ExtendLocalState<Core.AssessmentAttachment, { isLoaded?: boolean; }>;

interface CaseAssessmentWithAttachmentWithLocalDetailModel extends CM.CaseAssessmentWithAttachmentDetailModel {
    attachment: AttachmentWithLocalState;
}

interface CaseWithLocalAttachmentState extends CM.Case {
    assessments: CaseAssessmentWithAttachmentWithLocalDetailModel[];
}

@autoinject()
export class AssessmentSummaries extends ComponentBase {

    ////****************** Fields and Properties ************************************////

    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public targetId: string | null = null;
    @bindable.objectNull({ defaultBindingMode: bindingMode.toView })
    public config: IAssessmentsConfig | null = null;
    @bindable.objectNull({ defaultBindingMode: bindingMode.toView })
    public taskConfig: ITasksConfig | null = null;

    protected ignoreExpandCollapse: boolean = false;
    protected allExpanded: boolean = false;
    protected collapseAllDisabled: boolean = true;

    protected showHistory: boolean = false;

    protected id: guid = guid();

    /** Holds cases associated with a household case */
    protected cases: CaseWithLocalAttachmentState[] = [];

    ////****************** Element References ***************************************////

    protected $collapse!: ICollapse;
    protected $listElement!: HTMLElement;

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly service: AssessmentService,
        private readonly caseService: CaseService,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        if(!this.targetId) { this.logger.warn('Target ID was not bound'); }
        if(!this.config) { this.logger.warn('Assessment config was not bound'); }

        if(!!this.config) {
            this.service.configure(this.config);
        }

        if(!!this.targetId && !!this.config) {
            this.reload(this.targetId);
        }
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////

    public async reload(targetId: string): Promise<void> {
        if(!targetId) { return; }

        await this.loadCases();
    }

    protected updateCollapsed(): void {
        if(!this.ignoreExpandCollapse) {
            const counts = this.$collapse.getCounts();
            this.collapseAllDisabled = counts.open === 0;
        }
    }

    protected collapseAll(event: Event): void {
        try {
            this.ignoreExpandCollapse = true;
            this.collapseAllDisabled = true;
            this.allExpanded = false;
            this.$collapse.closeAll();
        }
        finally {
            this.ignoreExpandCollapse = false;
        }

        // remove focus from button
        if(event.srcElement instanceof HTMLElement) { event.srcElement.blur(); }
    }

    protected expandAll(event: Event): void {
        // ensure all attachments get loaded
        for(const c of this.cases) {
            for(const a of c.assessments) {
                if(!a.attachment._state) {
                    a.attachment._state = {};
                }
                a.attachment._state.isLoaded = true;
            }
        }

        try {
            this.ignoreExpandCollapse = true;
            this.allExpanded = true;
            this.collapseAllDisabled = false;
            this.$collapse.openAll();
        }
        finally {
            this.ignoreExpandCollapse = false;
        }

        // remove focus from button
        if(event.srcElement instanceof HTMLElement) { event.srcElement.blur(); }
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    public async loadCases(): Promise<void> {
        if(!this.targetId) return;

        const options: Partial<IQueryLoadOptions> = { all: true };

        const includes: string[] = [
            'member',
            'assessments'
        ];

        try {
            this.state.beginLoad();
            const response = await this.caseService.loadCasesByHouseholdCase(this.targetId, options, includes);

            this.cases = response.results.filter(c => {
                return c.assessments && c.assessments.length !== 0
            }) as CaseWithLocalAttachmentState[];
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

}
